import { zodResolver } from '@hookform/resolvers/zod';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form/form';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { AuthButton } from '@/features/auth/components/auth-button';
import { AuthForm } from '@/features/auth/components/auth-form';
import { AuthLayout } from '@/features/auth/components/auth-layout';
import { useCreateOrganisation } from '@/features/organisations/api/organisation-mutations';
import { countries, currencies, measurementSystems } from '@/lib/consts';

import { Route as SetupSuppliersRoute } from './setup-suppliers';

const formSchema = z.object({
  title: z.string().min(1, 'Name is required'),
  country: z.string().min(1, 'Country is required'),
  currency: z.enum(
    Object.keys(currencies) as [
      keyof typeof currencies,
      ...(keyof typeof currencies)[],
    ],
  ),
  measurement_system: z.enum(measurementSystems),
});

type OnboardFormValues = z.infer<typeof formSchema>;

const OnboardPage = () => {
  const navigate = useNavigate();

  const createOrganisation = useCreateOrganisation();

  const form = useForm<OnboardFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      title: '',
      currency: 'EUR',
      measurement_system: 'metric',
      country: 'IE',
    },
  });

  const handleSubmit = async (values: OnboardFormValues) => {
    form.clearErrors('root');

    try {
      const result = await createOrganisation.mutateAsync([
        {
          title: values.title,
          currency: values.currency,
          measurement_system: values.measurement_system,
          country: values.country,
        },
      ]);

      // Check if the organization was created successfully
      if (!result || result.length === 0) {
        form.setError('root', {
          type: 'manual',
          message: 'Failed to create organization. Please try again.',
        });
        return;
      }

      const organisation = result[0];

      await navigate({
        to: SetupSuppliersRoute.to,
        search: { organisationId: organisation.id },
      });
    } catch (error) {
      form.setError('root', {
        type: 'manual',
        message:
          error instanceof Error
            ? error.message
            : 'An unexpected error occurred. Please try again.',
      });
    }
  };

  const currencyOptions = Object.entries(currencies).map(
    ([value, { symbol, label }]) => ({
      value,
      label: `${symbol} ${label}`,
    }),
  );

  const measurementOptions = [
    { value: 'metric', label: 'Metric' },
    { value: 'imperial', label: 'Imperial' },
  ];

  return (
    <AuthLayout>
      <AuthForm
        onSubmit={form.handleSubmit(handleSubmit)}
        title="Create your Organisation"
        description="Fill in your organisation details to get started"
      >
        <Form {...form}>
          <div className="grid grid-rows-[auto_1fr] gap-4">
            {/* Error message area - only takes space when needed */}
            <div className="overflow-hidden">
              <div
                className={`transition-all duration-200 ${
                  form.formState.errors.root
                    ? 'max-h-20 opacity-100'
                    : 'max-h-0 opacity-0'
                }`}
              >
                <div className="rounded-md bg-red-50 p-4">
                  <div className="text-sm text-red-700">
                    {form.formState.errors.root?.message}
                  </div>
                </div>
              </div>
            </div>

            {/* Form fields area */}
            <div className="space-y-4">
              <FormField
                control={form.control}
                name="title"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Organisation Name</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Your organisation name"
                        autoComplete="organization"
                        className="h-12"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="country"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Country</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger className="h-12">
                          <SelectValue placeholder="Select a country" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {countries.map((country) => (
                          <SelectItem key={country.value} value={country.value}>
                            {country.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="currency"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Currency</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger className="h-12">
                          <SelectValue placeholder="Select a currency" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {currencyOptions.map((currency) => (
                          <SelectItem
                            key={currency.value}
                            value={currency.value}
                          >
                            {currency.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="measurement_system"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Measurement System</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger className="h-12">
                          <SelectValue placeholder="Select measurement system" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {measurementOptions.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>
        </Form>

        <AuthButton type="submit" disabled={createOrganisation.isPending}>
          {createOrganisation.isPending ? 'Creating...' : 'Create Organisation'}
        </AuthButton>
      </AuthForm>
    </AuthLayout>
  );
};

export const Route = createFileRoute('/_authenticated/onboard/')({
  component: OnboardPage,
});
